import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import {
  activateBusinessApp,
  businessAppQuery,
  businessAppSupportSuggestionsQuery,
  markBusinessAppReviewed,
  removeBusinessAppComponent,
  saveBusinessApp,
  saveBusinessAppSupport,
  type BusinessAppComponent,
  type BusinessAppDetail,
  type CustomerPerson,
  type NewPerson,
  type SupportSuggestions,
  type Written,
} from '../api/businessApps'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { draftProblem, OwnerPicker, saveOwnerDraft } from './OwnerPicker'
import { appUrl, failed } from './parts'
import { activeMarks, savedValues, SuggestedMark, SuggestionsNote, withSuggestions } from './support/prefill'

/**
 * COMPLETARE RAPIDA - fereastra "Complete" din banda si din lista de aplicatii.
 *
 * Coordonatoarea, 5 octombrie 2026: tot ce lipseste din checklist se
 * completeaza pe loc, fara sa intri pe fisa. Fereastra arata numai sectiunile
 * care lipsesc (cu "Show all") si salveaza prin ACELEASI capete ca fisa:
 *
 *  - service owner   `POST /business-apps/save` (randul intreg, ca Edit);
 *  - suport, program, escaladare
 *                    `POST /business-apps/{id}/support` (contactele existente
 *                    merg inapoi neschimbate, cele noi se adauga);
 *  - serverele       `POST /business-apps/components/{cid}/remove` pentru ce
 *                    s-a debifat, apoi `POST /business-apps/{id}/activate`;
 *  - reverificarea   `POST /business-apps/{id}/reviewed`.
 *
 * Dupa salvare se reincarca lista (banda, semnul rosu) si shell-ul (insigna
 * din bara), iar fereastra se inchide.
 *
 * Coordonatoarea, 6 octombrie 2026, "nu inteleg nimic din complete popup":
 * capul spune DE CE se completeaza si arata pasii numerotati cu progres;
 * fiecare pas are o propozitie "la ce foloseste"; serverele stau cate unul pe
 * rand, cu rolurile ca etichete si motivul gasirii scurt (bifele raman pe
 * componenta, deci se scot aceleasi componente ca inainte); "Confirm &
 * activate" a trecut in subsol, ca "Save & activate", cu acelasi apel.
 * Subsolul ramane pe ecran: corpul ferestrei se deruleaza, nu fereastra.
 *
 * Coordonatoarea, 6 octombrie 2026, mai tarziu: "scoate Save & next si scoate
 * si Save & activate, un singur Save care face si activare". Subsolul are
 * "Close" si un singur "Save": scrie ce s-a schimbat, scoate serverele
 * debifate si, pe o aplicatie "Pending review", o activeaza (cand a ramas
 * macar un server bifat) - aceeasi ordine de apeluri ca vechiul
 * "Save & activate".
 *
 * Coordonatoarea, 6 octombrie 2026, la "Service owner at the customer": omul
 * de la client nu e mereu in lista. Pasul are cautare si "+ Add a person"
 * (`OwnerPicker`); persoana noua se scrie intai ca persoana de contact a
 * clientului (`POST /business-apps/people/{companyId}`), apoi aplicatia o
 * primeste ca owner prin acelasi `/save`.
 *
 * Coordonatoarea, 6 octombrie 2026: "precompleteaza vendorii ... tot ce poti
 * afla despre ei". In pasii de suport, program si escaladare care lipsesc,
 * campurile goale pornesc cu propunerile din
 * `GET /business-apps/{id}/support-suggestions` (`support/prefill.tsx`), cu
 * semnul "Suggested - from ..." langa ele si "Clear suggestions" sus. Se scriu
 * numai la Save; pana atunci pasul e "Filled in - save to keep" sau, daca tot
 * lipseste ceva (contractul nu se propune niciodata), "To do".
 */

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MAX_NEW_CONTACTS = 3

type ContactRow = { name: string; role: string; phone: string; email: string; level: number; levelNote: string }

const blankContact = (level: number): ContactRow => ({ name: '', role: '', phone: '', email: '', level, levelNote: '' })
const filled = (c: ContactRow) => c.name.trim() !== '' || c.email.trim() !== '' || c.phone.trim() !== ''

export function QuickCompleteModal({ appId, onClose }: { appId: number; onClose(): void }) {
  const page = useQuery({ ...businessAppQuery(appId), refetchOnWindowFocus: false })
  /* Propunerile de suport: fereastra le asteapta (ca sa porneasca formularul
     cu ele), dar o eroare inseamna doar "fara propuneri". */
  const suggestions = useQuery(businessAppSupportSuggestionsQuery(appId))
  /* Formularul se reface din fisa proaspata dupa fiecare salvare, nu la orice
     reincarcare: o reincarcare in fundal nu sterge ce scrie omul. */
  const [round, setRound] = useState(0)

  if (!page.data || (page.data as { error?: string }).error || suggestions.isPending) {
    return (
      <Modal id="quickCompleteModal" open large onClose={onClose} title="Complete application">
        {page.error ? <div className="alert alert-danger">{failed(page.error)}</div> : (page.data as { message?: string } | undefined)?.message ?? <Spinner />}
      </Modal>
    )
  }

  return (
    <QuickCompleteForm
      key={`${appId}-${round}`}
      page={page.data}
      suggestions={suggestions.data}
      onClose={onClose}
      onStay={() => void page.refetch().then(() => setRound((r) => r + 1))}
    />
  )
}

function QuickCompleteForm({
  page,
  suggestions,
  onClose,
  onStay,
}: {
  page: BusinessAppDetail
  suggestions?: SupportSuggestions
  onClose(): void
  onStay(): void
}) {
  const app = page.app
  const query = useQueryClient()
  const missing = new Set((app.missing ?? []).map((m) => m.key))
  const pending = app.lifecycle === 'pending_review'
  const [showAll, setShowAll] = useState(missing.size === 0)
  const shows = (key: string) => showAll || missing.has(key)

  const s = page.support
  const saved = savedValues(s)
  const initial = {
    owner: app.ownerContactId ?? 0,
    vendor: saved.vendor,
    contract: s.contractNo,
    portal: saved.portal,
    days: saved.days,
    from: saved.from,
    to: saved.to,
    tz: saved.tz,
    hours: saved.hours,
  }
  /* Propunerile, numai in pasii care lipsesc (si deci se vad). */
  const [prefill] = useState(() =>
    withSuggestions(s, suggestions, {
      vendor: missing.has('support'),
      portal: missing.has('support'),
      schedule: missing.has('schedule'),
      hours: missing.has('schedule'),
      needs: false,
      contacts: missing.has('escalation'),
    }),
  )
  const start = prefill.values
  const startContacts = prefill.contacts.slice(0, MAX_NEW_CONTACTS)
  const [cleared, setCleared] = useState(false)
  const [owner, setOwner] = useState(initial.owner)
  const [ownerDraft, setOwnerDraft] = useState<NewPerson | null>(null)
  /* Persoana scrisa deja la client in fereastra asta (daca salvarea de dupa a
     cazut): sta in lista pana se reincarca fisa, ca sa nu se scrie de doua ori. */
  const [addedPeople, setAddedPeople] = useState<CustomerPerson[]>([])
  const [vendor, setVendor] = useState(start.vendor)
  const [contract, setContract] = useState(initial.contract)
  const [portal, setPortal] = useState(start.portal)
  const [days, setDays] = useState<number[]>(start.days)
  const [from, setFrom] = useState(start.from)
  const [to, setTo] = useState(start.to)
  const [tz, setTz] = useState(start.tz)
  const [hours, setHours] = useState(start.hours)
  const [added, setAdded] = useState<ContactRow[]>(startContacts.length > 0 ? startContacts : missing.has('escalation') ? [blankContact(1)] : [])
  const [keep, setKeep] = useState<Record<number, boolean>>(() => Object.fromEntries(page.components.map((c) => [c.id, true])))
  const [busy, setBusy] = useState(false)

  /* "Clear suggestions": inapoi la ce e in fisa (si la programul implicit). */
  const clearSuggestions = () => {
    if (marks.vendor) setVendor(initial.vendor)
    if (marks.portal) setPortal(initial.portal)
    if (marks.schedule) {
      setDays(initial.days)
      setFrom(initial.from)
      setTo(initial.to)
      setTz(initial.tz)
    }
    if (marks.hours) setHours(initial.hours)
    if (marks.contacts) setAdded(missing.has('escalation') ? [blankContact(1)] : [])
    setCleared(true)
  }

  const toggleDay = (d: number) => setDays((list) => (list.includes(d) ? list.filter((x) => x !== d) : [...list, d].sort()))
  const setRow = (i: number, patch: Partial<ContactRow>) => setAdded((list) => list.map((c, j) => (j === i ? { ...c, ...patch } : c)))

  const removals = page.components.filter((c) => keep[c.id] === false)
  const kept = page.components.length - removals.length
  const ownerDrafted = ownerDraft !== null && ownerDraft.name.trim() !== ''
  const ownerChanged = owner !== initial.owner || ownerDrafted
  const marks = activeMarks(
    prefill.marks,
    start,
    { vendor, portal, days, from, to, tz, hours, needs: s.caseNeeds },
    JSON.stringify(added) === JSON.stringify(startContacts),
    cleared,
  )
  const suggesting = Object.keys(marks).length > 0
  /* O propunere inca pe ecran se scrie la Save, chiar daca e egala cu
     programul implicit. */
  const supportChanged =
    suggesting ||
    vendor !== initial.vendor ||
    contract !== initial.contract ||
    portal !== initial.portal ||
    days.join(',') !== initial.days.join(',') ||
    from !== initial.from ||
    to !== initial.to ||
    tz !== initial.tz ||
    hours !== initial.hours ||
    added.some(filled)
  /* Programul implicit (L-V 09-17) e doar o propunere: se scrie numai cand
     omul a atins suportul, nu la o simpla schimbare de owner. */

  const ok = (result: Written) => {
    if (result.status === 'OK') return true
    toast(result.message, 'Applications', 'error')
    return false
  }

  /** Scrie ce s-a schimbat, prin capetele fisei. false = s-a oprit la o eroare. */
  const persist = async (): Promise<boolean> => {
    if (ownerChanged) {
      let ownerId = owner
      if (ownerDraft !== null && ownerDrafted) {
        const person = await saveOwnerDraft(app.companyId, ownerDraft)
        ownerId = person.id
        setAddedPeople((list) => [...list, person])
        setOwner(person.id)
        setOwnerDraft(null)
      }
      const saved = await saveBusinessApp({
        ID: app.id,
        company_id: app.companyId,
        name: app.name,
        vendor: app.vendor,
        version: app.version,
        criticality: app.criticality,
        owner_contact_id: ownerId,
        description: app.description,
        notes: app.notes,
      })
      if (!ok(saved)) return false
    }
    if (supportChanged) {
      const saved = await saveBusinessAppSupport(app.id, {
        support_vendor: vendor,
        support_portal_url: portal,
        support_contract_no: contract,
        support_days: days,
        support_from: from,
        support_to: to,
        support_tz: tz,
        support_response_hours: hours,
        support_case_needs: s.caseNeeds,
        contacts: [
          ...page.contacts.map((c) => ({ name: c.name, role: c.role, phone: c.phone, email: c.email, level: c.level, levelNote: c.levelNote })),
          ...added.filter(filled),
        ],
      })
      if (!ok(saved)) return false
    }
    for (const c of removals) {
      if (!ok(await removeBusinessAppComponent(c.id))) return false
    }
    return true
  }

  const refresh = () => {
    void query.invalidateQueries({ queryKey: ['business-apps'] })
    void query.invalidateQueries({ queryKey: ['shell'] })
  }

  /* Coordonatoarea, 6 octombrie 2026: "un singur Save care face si activare".
     Save scrie ce s-a schimbat, scoate serverele debifate si, pe o aplicatie
     "Pending review" pe care omul o poate edita, o activeaza - daca a ramas
     macar un server bifat. Fara niciun server salveaza si spune de ce n-a
     activat. Pe o aplicatie deja activa doar salveaza. */
  const activates = pending && page.canEdit

  const run = (then: 'close' | 'stay', after?: (id: number) => Promise<Written>) => {
    /* Un "Add a person" deschis si lasat gol nu opreste nimic; unul inceput,
       da - altfel s-ar pierde in tacere ce a scris omul. */
    const touched = ownerDraft !== null && Object.values(ownerDraft).some((v) => v.trim() !== '')
    const problem = touched ? draftProblem(ownerDraft) : null
    if (problem !== null) {
      toast(problem, 'Applications', 'error')
      return
    }
    setBusy(true)
    persist()
      .then(async (saved) => {
        if (!saved) return
        if (after && !ok(await after(app.id))) return
        const changed = after !== undefined || ownerChanged || supportChanged || removals.length > 0
        if (after === activateBusinessApp) toast('Saved and activated.', 'Applications')
        else if (then === 'close' && activates && kept < 1)
          toast('Saved, but not activated: keep at least one server - the application needs one to be active.', 'Applications', 'warning')
        else if (changed) toast('Saved.', 'Applications')
        refresh()
        if (then === 'stay') onStay()
        else onClose()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  /* Starea fiecarui pas: `done` din checklistul serverului, `filled` cand omul
     a completat in fereastra dar n-a salvat inca, altfel `todo`. */
  const escalationReady = [...page.contacts, ...added].some((c) => c.level >= 1 && (c.phone.trim() !== '' || c.email.trim() !== ''))
  const filledNow: Record<string, boolean> = {
    owner: owner > 0 || ownerDrafted,
    support: vendor.trim() !== '' && contract.trim() !== '',
    schedule: days.length > 0 && from !== '' && to !== '' && Number(hours) > 0,
    escalation: escalationReady,
  }
  const steps = completionSteps(app, missing)
  const stateOf = (key: string): StepState => (!missing.has(key) ? 'done' : filledNow[key] === true ? 'filled' : 'todo')
  const stepNo = (key: string) => steps.findIndex((st) => st.key === key) + 1
  const doneCount = steps.filter((st) => stateOf(st.key) === 'done').length
  const filledCount = steps.filter((st) => stateOf(st.key) === 'filled').length
  const todoCount = steps.length - doneCount
  const groups = serverGroups(page.components)

  const nothingShown = !['components', 'owner', 'support', 'schedule', 'escalation', 'review'].some((k) => shows(k))
  const jump = (key: string) => {
    if (!shows(key)) setShowAll(true)
    requestAnimationFrame(() => document.getElementById(SECTION_IDS[key] ?? '')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }))
  }

  return (
    <Modal
      id="quickCompleteModal"
      open
      large
      onClose={onClose}
      title={
        <>
          Complete {app.name}
          {app.companyName !== '' ? <span className="adm-muted small"> &middot; {app.companyName}</span> : null}
        </>
      }
      footer={
        <div className="app-qc-footer">
          <p className="app-qc-footer-note">
            {activates
              ? 'Save keeps what you filled in, removes the unticked servers and makes the application active. Steps you leave empty stay on the to-do list.'
              : 'Only what you changed is saved. Steps you leave empty stay on the to-do list.'}
          </p>
          <div className="app-qc-footer-buttons">
            <a className="btn btn-link" href={appUrl(app.id)} id="qcOpenPage" target="_blank" rel="noopener noreferrer">
              Open the application page
            </a>
            <span className="app-qc-footer-spacer"></span>
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              id="qcSave"
              disabled={busy}
              title={activates ? (kept < 1 ? 'Saves only: keep at least one server to make the application active' : 'Save, remove the unticked servers and make the application active') : undefined}
              onClick={() => run('close', activates && kept >= 1 ? activateBusinessApp : undefined)}
            >
              Save
            </button>
          </div>
        </div>
      }
    >
      <div className={`app-qc-summary${todoCount === 0 ? ' is-complete' : ''}`} id="qcSummary">
        <div className="app-qc-summary-text">
          <p className="app-qc-intro">
            {todoCount === 0
              ? `${app.name} has everything we need to monitor it and escalate when it goes down.`
              : pending
                ? `We found ${app.name} automatically on the customer's machines. To monitor it and escalate correctly when it goes down, we still need ${todoCount === 1 ? '1 thing' : `${todoCount} things`}:`
                : `To escalate correctly when ${app.name} goes down, we still need ${todoCount === 1 ? '1 thing' : `${todoCount} things`}:`}
          </p>
          <div className="app-qc-progress" aria-label={`${doneCount} of ${steps.length} done`}>
            <div className="app-qc-progress-bar">
              <span style={{ width: `${steps.length > 0 ? Math.round((doneCount / steps.length) * 100) : 100}%` }}></span>
            </div>
            <span className="app-qc-progress-label">
              {doneCount} of {steps.length} done
              {filledCount > 0 ? ` · ${filledCount} filled in, not saved yet` : ''}
            </span>
          </div>
        </div>
        <ol className="app-qc-steps">
          {steps.map((st, i) => {
            const state = stateOf(st.key)
            return (
              <li key={st.key} className={`is-${state}`} data-key={st.key}>
                <button type="button" className="app-qc-step-link" onClick={() => jump(st.key)}>
                  <StepMark state={state} no={i + 1} />
                  <span>{STEP_TITLES[st.key] ?? st.label}</span>
                </button>
              </li>
            )
          })}
        </ol>
        {suggesting ? <SuggestionsNote id="qcSuggested" onClear={clearSuggestions} /> : null}
        <label className="app-qc-showall">
          <input type="checkbox" id="qcShowAll" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show completed steps too
        </label>
      </div>

      {shows('components') ? (
        <QcSection
          id="qcComponents"
          no={stepNo('components')}
          state={stateOf('components')}
          title={pending ? 'Confirm the servers' : 'Servers the application runs on'}
          why={
            pending
              ? 'We found these servers running the application. Untick any that are wrong - they will be removed from the application.'
              : 'The servers the application runs on - we watch them and know what is affected when one goes down. Untick any that are wrong - they will be removed from the application.'
          }
        >
          {page.components.length === 0 ? (
            <p className="adm-muted">
              No server recorded yet.{' '}
              <a href={`${appUrl(app.id)}#app-sec-components`} target="_blank" rel="noopener noreferrer">Add the servers on the application page</a>.
            </p>
          ) : (
            <ul className="app-qc-servers">
              {groups.map((g) => (
                <ServerRow key={g.key} group={g} keep={keep} setKeep={setKeep} />
              ))}
            </ul>
          )}
          {activates ? (
            <p className="app-qc-hint">
              <i className="fas fa-info-circle" aria-hidden="true"></i> When the list is right, press <strong>Save</strong>: the unticked servers are removed and
              the application moves from &quot;Pending review&quot; to Active.
            </p>
          ) : null}
        </QcSection>
      ) : null}

      {shows('owner') ? (
        <QcSection
          id="qcOwner"
          no={stepNo('owner')}
          state={stateOf('owner')}
          title="Service owner at the customer"
          why="The person at the customer who decides for this application - we tell them when it goes down and agree maintenance with them. Pick them from the customer's people or add them if they are not there."
        >
          <label htmlFor="qcOwnerSelect" className="sr-only">
            Service owner
          </label>
          <OwnerPicker
            selectId="qcOwnerSelect"
            people={[...page.people, ...addedPeople.filter((a) => !page.people.some((p) => p.id === a.id))]}
            value={owner}
            onChange={setOwner}
            draft={ownerDraft}
            onDraft={setOwnerDraft}
            emptyLabel="- choose a person -"
            customerName={app.companyName}
            disabled={busy}
          />
        </QcSection>
      ) : null}

      {shows('support') ? (
        <QcSection
          id="qcSupport"
          no={stepNo('support')}
          state={stateOf('support')}
          title="Support vendor and contract"
          why="The company that helps when the problem is in the software, not in the server - we escalate to them."
        >
          <div className="row">
            <div className="col-sm-6 form-group">
              <label htmlFor="qcVendor">Vendor / partner</label>
              <SuggestedMark field="vendor" label={marks.vendor} />
              <input id="qcVendor" className="form-control" placeholder="e.g. TotalSoft" maxLength={190} value={vendor} onChange={(e) => setVendor(e.target.value)} />
            </div>
            <div className="col-sm-6 form-group">
              <label htmlFor="qcContract">Contract number</label>
              <input id="qcContract" className="form-control" placeholder="e.g. CT-2024-118" maxLength={100} value={contract} onChange={(e) => setContract(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="qcPortal">
              Their ticket portal <span className="adm-muted">(optional)</span>
            </label>
            <SuggestedMark field="portal" label={marks.portal} />
            <input id="qcPortal" className="form-control" placeholder="https://support.vendor.com" maxLength={500} value={portal} onChange={(e) => setPortal(e.target.value)} />
          </div>
        </QcSection>
      ) : null}

      {shows('schedule') ? (
        <QcSection
          id="qcSchedule"
          no={stepNo('schedule')}
          state={stateOf('schedule')}
          title="Support hours and response time"
          why="When the vendor answers and how fast - this is how we tell whether they are late."
        >
          <div className="form-group">
            <label className="app-qc-sublabel">Days they answer</label>
            <SuggestedMark field="schedule" label={marks.schedule} />
            <div>
              {DAY_NAMES.map((name, i) => (
                <label key={name} className="checkbox-inline">
                  <input type="checkbox" checked={days.includes(i + 1)} onChange={() => toggleDay(i + 1)} /> {name}
                </label>
              ))}
            </div>
          </div>
          <div className="row">
            <div className="col-sm-3 form-group">
              <label htmlFor="qcFrom">From</label>
              <input id="qcFrom" type="time" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="col-sm-3 form-group">
              <label htmlFor="qcTo">To</label>
              <input id="qcTo" type="time" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="col-sm-3 form-group">
              <label htmlFor="qcTz">Time zone</label>
              <input id="qcTz" className="form-control" placeholder="e.g. Europe/Bucharest" value={tz} onChange={(e) => setTz(e.target.value)} />
            </div>
            <div className="col-sm-3 form-group">
              <label htmlFor="qcHours">Response time (hours)</label>
              <SuggestedMark field="hours" label={marks.hours} />
              <input id="qcHours" type="number" min={1} className="form-control" placeholder="e.g. 4" value={hours} onChange={(e) => setHours(e.target.value)} />
            </div>
          </div>
        </QcSection>
      ) : null}

      {shows('escalation') ? (
        <QcSection
          id="qcEscalation"
          no={stepNo('escalation')}
          state={stateOf('escalation')}
          title="Escalation contact at the vendor"
          why="Who we call at the vendor when the application is down. Level 1 is called first, then level 2 and 3 - each needs a phone or an email."
        >
          {page.contacts.length > 0 ? (
            <p className="app-qc-recorded">
              <span className="adm-muted">Already recorded:</span>{' '}
              {page.contacts.map((c, i) => (
                <span key={i} className="adm-pill">
                  {c.name}
                  {c.level > 0 ? ` · level ${c.level}` : ' · not on the path'}
                </span>
              ))}
            </p>
          ) : null}
          {added.length > 0 && marks.contacts ? (
            <p className="app-qc-suggested-contacts">
              <SuggestedMark field="contacts" label={marks.contacts} />
            </p>
          ) : null}
          {added.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-condensed app-qc-contacts">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Level</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {added.map((c, i) => (
                    <tr key={i}>
                      <td>
                        <select className="form-control input-sm" aria-label="Level" value={c.level} onChange={(e) => setRow(i, { level: Number(e.target.value) })}>
                          <option value={0}>Not on path</option>
                          <option value={1}>Level 1</option>
                          <option value={2}>Level 2</option>
                          <option value={3}>Level 3</option>
                        </select>
                      </td>
                      <td>
                        <input className="form-control input-sm" aria-label="Name" placeholder="e.g. Andrei Popescu" value={c.name} onChange={(e) => setRow(i, { name: e.target.value })} />
                      </td>
                      <td>
                        <input className="form-control input-sm" aria-label="Role" placeholder="e.g. support desk" value={c.role} onChange={(e) => setRow(i, { role: e.target.value })} />
                      </td>
                      <td>
                        <input className="form-control input-sm" aria-label="Phone" placeholder="+40 21 000 0000" value={c.phone} onChange={(e) => setRow(i, { phone: e.target.value })} />
                      </td>
                      <td>
                        <input className="form-control input-sm" aria-label="Email" type="email" placeholder="support@vendor.com" value={c.email} onChange={(e) => setRow(i, { email: e.target.value })} />
                      </td>
                      <td>
                        <button type="button" className="btn btn-default btn-xs" aria-label="Remove row" onClick={() => setAdded((list) => list.filter((_, j) => j !== i))}>
                          <i className="fa fa-times"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {added.length < MAX_NEW_CONTACTS ? (
            <button
              type="button"
              className="btn btn-default btn-sm"
              id="qcContactAdd"
              onClick={() => setAdded((list) => [...list, blankContact(Math.min(3, list.length + 1))])}
            >
              <i className="fas fa-plus"></i> Add a contact
            </button>
          ) : null}
        </QcSection>
      ) : null}

      {missing.has('review') ? (
        <QcSection
          id="qcReview"
          no={stepNo('review')}
          state="todo"
          title="Yearly review"
          why="Once a year someone checks that the servers, the owner and the support details are still right."
        >
          <p className="app-qc-hint">Check the steps above (tick &quot;Show completed steps too&quot; to see them all), then mark it reviewed.</p>
          <button type="button" id="qcReviewed" className="btn btn-primary btn-sm" disabled={busy} onClick={() => run('stay', markBusinessAppReviewed)}>
            <i className="fas fa-clipboard-check"></i> Mark reviewed
          </button>
        </QcSection>
      ) : null}

      {nothingShown ? <p className="adm-muted app-qc-empty">Everything is filled in. Tick &quot;Show completed steps too&quot; to change something.</p> : null}
    </Modal>
  )
}

type StepState = 'done' | 'filled' | 'todo'

/** Titlul scurt al pasului, in lista din cap si pe sectiune. */
const STEP_TITLES: Record<string, string> = {
  components: 'Confirm the servers',
  owner: 'Service owner at the customer',
  support: 'Support vendor and contract',
  schedule: 'Support hours and response time',
  escalation: 'Escalation contact at the vendor',
  review: 'Yearly review',
}

const SECTION_IDS: Record<string, string> = {
  components: 'qcComponents',
  owner: 'qcOwner',
  support: 'qcSupport',
  schedule: 'qcSchedule',
  escalation: 'qcEscalation',
  review: 'qcReview',
}

/**
 * Pasii din capul ferestrei: checklistul serverului fara cele doar
 * recomandate (runbook-ul nu se completeaza aici), plus reverificarea cand e
 * scadenta. Fara checklist (lista veche), numai ce lipseste.
 */
function completionSteps(app: BusinessAppDetail['app'], missing: Set<string>): { key: string; label: string }[] {
  const base = (app.checklist ?? []).filter((c) => !c.recommended && SECTION_IDS[c.key] !== undefined).map((c) => ({ key: c.key, label: c.label }))
  const list = base.length > 0 ? base : (app.missing ?? []).filter((m) => m.key !== 'review').map((m) => ({ key: m.key, label: m.label }))
  return missing.has('review') ? [...list, { key: 'review', label: 'Review due' }] : list
}

function StepMark({ state, no }: { state: StepState; no: number }) {
  return (
    <span className={`app-qc-mark is-${state}`} aria-hidden="true">
      {state === 'done' ? <i className="fas fa-check"></i> : no > 0 ? no : ''}
    </span>
  )
}

const STATE_PILL: Record<StepState, { cls: string; text: string }> = {
  done: { cls: 'adm-pill adm-pill-ok', text: 'Done' },
  filled: { cls: 'adm-pill adm-pill-running', text: 'Filled in - save to keep' },
  todo: { cls: 'adm-pill adm-pill-bad', text: 'To do' },
}

function QcSection({ id, no, state, title, why, children }: { id: string; no: number; state: StepState; title: string; why: string; children: ReactNode }) {
  const pill = STATE_PILL[state]
  return (
    <section id={id} className={`app-qc-section${state === 'done' ? ' is-done' : ' is-missing'} is-${state}`}>
      <header className="app-qc-section-head">
        <StepMark state={state} no={no} />
        <h5>
          {no > 0 ? <span className="app-qc-stepno">Step {no}. </span> : null}
          {title}
        </h5>
        <span className={`${pill.cls} app-qc-state`}>{pill.text}</span>
      </header>
      <p className="app-qc-why">{why}</p>
      <div className="app-qc-section-body">{children}</div>
    </section>
  )
}

/* ---------------------------------------------------------------------------
 * Serverele: un rand pe server, cu toate rolurile lui. Bifele raman pe
 * componenta (`keep[c.id]`), deci la salvare pleaca exact aceleasi
 * `components/{cid}/remove` ca inainte - gruparea e numai pe ecran.
 * ------------------------------------------------------------------------- */

type ServerGroup = { key: string; name: string; items: BusinessAppComponent[] }

export function serverGroups(components: BusinessAppComponent[]): ServerGroup[] {
  const groups = new Map<string, ServerGroup>()
  for (const c of components) {
    const key = c.deviceId !== null ? `d${c.deviceId}` : c.ciId !== null ? `c${c.ciId}` : `n${c.name.toLowerCase()}`
    const g = groups.get(key)
    if (g) g.items.push(c)
    else groups.set(key, { key, name: c.name, items: [c] })
  }
  return [...groups.values()]
}

/**
 * "De ce l-am gasit", scurt: nota detectiei e `Found: service "X"; software
 * "Y"; device name "Z"`. Numele aparatului se vede deja pe rand, deci cade;
 * restul se aduna, fara repetari, pe toate rolurile serverului. O nota scrisa
 * de mana (fara `Found:`) ramane cum e.
 */
export function matchedSummary(notes: string[]): string {
  const parts: string[] = []
  let manual = false
  for (const note of notes) {
    const text = note.trim()
    if (text === '') continue
    if (!/^found:/i.test(text)) {
      manual = true
      if (!parts.includes(text)) parts.push(text)
      continue
    }
    for (const bit of text.replace(/^found:\s*/i, '').split(/;\s*/)) {
      const b = bit.trim()
      if (b === '' || /^device name\b/i.test(b) || parts.includes(b)) continue
      parts.push(b)
    }
  }
  if (parts.length === 0) return ''
  return (manual ? '' : 'matched: ') + parts.join(' · ')
}

const ROLE_CLASS: Record<number, string> = { 1: 'is-db', 2: 'is-app', 3: 'is-web', 4: 'is-client' }

function ServerRow({
  group,
  keep,
  setKeep,
}: {
  group: ServerGroup
  keep: Record<number, boolean>
  setKeep: (fn: (k: Record<number, boolean>) => Record<number, boolean>) => void
}) {
  const [open, setOpen] = useState(false)
  const on = group.items.filter((c) => keep[c.id] !== false).length
  const all = on === group.items.length
  const none = on === 0
  const several = group.items.length > 1
  const reason = matchedSummary(group.items.map((c) => c.note))
  const full = group.items
    .filter((c) => c.note.trim() !== '')
    .map((c) => `${c.roleLabel}: ${c.note}`)
    .join('\n')
  const setAll = (value: boolean) => setKeep((k) => ({ ...k, ...Object.fromEntries(group.items.map((c) => [c.id, value])) }))

  return (
    <li data-id={group.items[0].id} data-ids={group.items.map((c) => c.id).join(',')} className={`app-qc-server${none ? ' is-off' : ''}`}>
      <div className="app-qc-server-main">
        <label className="app-qc-server-name">
          <input
            type="checkbox"
            checked={!none}
            ref={(el) => {
              if (el) el.indeterminate = !all && !none
            }}
            onChange={(e) => setAll(e.target.checked)}
            aria-label={`Keep ${group.name}`}
          />{' '}
          <strong>{group.name}</strong>
        </label>
        <span className="app-qc-roles">
          {group.items.map((c) =>
            several ? (
              <label key={c.id} className={`adm-pill app-qc-role ${ROLE_CLASS[c.role] ?? 'is-other'}${keep[c.id] === false ? ' is-off' : ''}`} title={`Keep ${group.name} as ${c.roleLabel}`}>
                <input type="checkbox" data-cid={c.id} checked={keep[c.id] !== false} onChange={(e) => setKeep((k) => ({ ...k, [c.id]: e.target.checked }))} /> {c.roleLabel}
              </label>
            ) : (
              <span key={c.id} className={`adm-pill app-qc-role ${ROLE_CLASS[c.role] ?? 'is-other'}`}>
                {c.roleLabel}
              </span>
            ),
          )}
        </span>
      </div>
      {reason !== '' ? (
        <button type="button" className={`app-qc-reason${open ? ' is-open' : ''}`} title={full} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? full.split('\n').map((line, i) => <span key={i}>{line}</span>) : reason}
        </button>
      ) : null}
    </li>
  )
}
